import { useRequiredContext } from "@redotech/react-util/context";
import { useLoad } from "@redotech/react-util/load";
import { RedoMerchantRpcClientContext } from "@redotech/redo-merchant-app-common/rpc-client";
import { TeamContext } from "@redotech/redo-merchant-app-common/team";
import {
  ICarrierAccount,
  RedoCarrierName,
} from "@redotech/redo-model/carrier-account/carrier-account";
import {
  NotificationBannerAction,
  NotificationBannerItem,
} from "@redotech/redo-web/arbiter-components/notification/redo-notification-banner";
import { useContext } from "react";
import { useLocation } from "react-router-dom";
import { ActionRequiredSeverity } from "./action-required-notification";

const FEDEX_MFA_DEADLINE = new Date("2026-05-01T00:00:00Z");

function getDaysUntilDeadline(): number {
  const now = new Date();
  const diffMs = FEDEX_MFA_DEADLINE.getTime() - now.getTime();
  return Math.max(0, Math.ceil(diffMs / (1000 * 60 * 60 * 24)));
}

export function useFedexMfaBanner(): {
  severity: ActionRequiredSeverity;
  item: NotificationBannerItem;
} | null {
  const team = useContext(TeamContext);
  const location = useLocation();
  const rpcClient = useRequiredContext(RedoMerchantRpcClientContext);

  const newOmsEnabled = team?.settings?.outboundLabels?.newOmsEnabled ?? false;
  const isOutboundLabelsRoute = location.pathname.includes("/outbound-labels");
  const isOutboundLabelsSettingsRoute =
    location.pathname.includes("/settings/oms") ||
    location.pathname.includes("/settings/carriers");

  const shouldCheck =
    newOmsEnabled && (isOutboundLabelsRoute || isOutboundLabelsSettingsRoute);

  const carrierAccountsLoad = useLoad<ICarrierAccount[]>(
    async (signal) => {
      if (!shouldCheck) {
        return [];
      }
      const result = await rpcClient.getCarrierAccounts(null, { signal });
      return result.merchantCarrierAccounts ?? [];
    },
    [rpcClient, shouldCheck],
  );

  if (!shouldCheck) {
    return null;
  }

  if (carrierAccountsLoad.pending || carrierAccountsLoad.error) {
    return null;
  }

  const carrierAccounts = carrierAccountsLoad.value;
  if (!carrierAccounts) {
    return null;
  }

  const nonMfaFedexAccounts = carrierAccounts.filter(
    (account: ICarrierAccount) =>
      (account.redoCarrierName === RedoCarrierName.FEDEX ||
        account.redoCarrierName === RedoCarrierName.FEDEX_SMARTPOST) &&
      "mfaComplete" in account &&
      !account.mfaComplete,
  );

  if (nonMfaFedexAccounts.length === 0) {
    return null;
  }

  const daysLeft = getDaysUntilDeadline();

  const message =
    daysLeft > 0
      ? `FedEx is introducing multi-factor authentication (MFA). To keep your account active, please authenticate within the next ${daysLeft} days.`
      : "FedEx is introducing multi-factor authentication (MFA). To keep your account active, please authenticate now.";

  const action: NotificationBannerAction = {
    actionType: "link",
    text: "Authenticate here",
    href: "/settings/carriers?carriersTab=merchantCarriers",
    internal: true,
    target: "_self",
  };

  return {
    severity: ActionRequiredSeverity.High,
    item: {
      id: "fedex-mfa-migration",
      type: "error",
      bgColor: "error-secondary",
      borderColor: "error-solid",
      message,
      actions: [action],
    },
  };
}
